import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAuthUser } from '@/features/auth';
import type {
  ConsultarRespostaInput,
  ReciboRespostaPendencia,
  RegistrarReciboInput,
} from '../types/reciboRespostaTypes';
import { liberarChaveResposta } from '../utils/chavesResposta';

import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';

import type { CertificadoAcessosArbitral, CertificadoGerado } from '@/features/arbitral-detalhes';
import type { ListarModelosEmissaoInput, ModelosEmissaoPagina } from '../types/modelosEmissaoTypes';

import type {
  BaixarCertificadoInput,
  BaixarDocumentoInput,
  ConclusoRespostaContexto,
  CriarAndamentoInput,
  EmissaoCategoria,
  EmissaoModeloPreview,
  EmissaoRascunho,
  ObterProcessoResult,
  PainelAndamento,
  PainelDashboardResponse,
  PainelDocumento,
  PainelFinanceiroResponse,
  PainelListQuery,
  PainelNotificacaoFeed,
  PainelPendencia,
  PainelProcessoItem,
  PublicarDocumentoInput,
  SalvarRascunhoInput,
  DadosContratoConferidos,
} from '../types/painelListTypes';
import { arbitroWorkspaceService, painelProcessosService } from './services';

export const registrarReciboResposta = createAction<RegistrarReciboInput>(
  'arbitro/registrarReciboResposta',
);

export const consultarRespostaPainel = createAsyncThunk<
  ReciboRespostaPendencia | null,
  ConsultarRespostaInput,
  { state: RootState; rejectValue: string }
>('arbitro/consultarRespostaPainel', async (input, { getState, rejectWithValue, requestId }) => {
  const usuarioInicial = selectAuthUser(getState())?.id;
  try {
    const recibo = await painelProcessosService.consultarResposta(input);
    const usuario = selectAuthUser(getState());
    if (
      usuario?.id !== usuarioInicial ||
      getState().arbitro.consultasRespostasRequestIds[`${input.id}:${input.pendenciaId}`] !==
        requestId
    )
      return null;
    if (recibo?.statusAtual === 'ABERTA' && usuario)
      liberarChaveResposta(usuario.id, input.id, input.pendenciaId, input.clientRequestId);
    return recibo;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const listarProcessosPainel = createAsyncThunk(
  'arbitro/listarProcessosPainel',
  async (query: PainelListQuery, { rejectWithValue }) => {
    try {
      return await painelProcessosService.listar(query);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const obterProcessoPainel = createAsyncThunk<
  ObterProcessoResult,
  string,
  { rejectValue: string }
>('arbitro/obterProcessoPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obter(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const listarAndamentosPainel = createAsyncThunk<
  PainelAndamento[],
  string,
  { rejectValue: string }
>('arbitro/listarAndamentosPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarAndamentos(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const criarAndamentoPainel = createAsyncThunk<
  PainelAndamento[] | null,
  CriarAndamentoInput,
  { state: RootState; rejectValue: string }
>(
  'arbitro/criarAndamentoPainel',
  async (input, { dispatch, getState, rejectWithValue, requestId }) => {
    const usuarioInicial = getState().auth?.user?.id;
    const envioAtual = () =>
      getState().auth?.user?.id === usuarioInicial &&
      getState().arbitro.andamentoCriacaoRequestId === requestId;
    try {
      const recibo = await painelProcessosService.criarAndamento(input);
      if (!envioAtual())
        return rejectWithValue(
          'A sessão mudou durante o envio. Consulte o resultado na sessão de origem.',
        );
      // O recibo confirma a publicação antes da releitura: falha no GET não autoriza outro POST.
      if (recibo)
        dispatch(registrarReciboResposta({ id: input.id, descricao: input.descricao, recibo }));
    } catch (error) {
      if (!envioAtual())
        return rejectWithValue(
          'A sessão mudou durante o envio. Consulte o resultado na sessão de origem.',
        );
      if (!input.pendenciaId || !input.clientRequestId)
        return rejectWithValue(getErrorMessage(error));
      const consulta = await dispatch(
        consultarRespostaPainel({
          id: input.id,
          pendenciaId: input.pendenciaId,
          clientRequestId: input.clientRequestId,
        }),
      );
      if (!consultarRespostaPainel.fulfilled.match(consulta) || !consulta.payload)
        return rejectWithValue(getErrorMessage(error));
      if (consulta.payload.statusAtual === 'ABERTA')
        return rejectWithValue(
          'O envio anterior foi registrado, mas a pendência foi reaberta. Confira a resposta antes de iniciar outro envio.',
        );
    }
    const state = getState().arbitro;
    const processoAtual = state.andamentosSolicitadoProcessoId ?? state.detalheItem?.id;
    if (processoAtual && processoAtual !== input.id) return null;
    if (input.pendenciaId && state.respostasPendencias[input.id]?.[input.pendenciaId]?.recibo) {
      void dispatch(listarPendenciasPainel(input.id));
    }
    if (input.arquivos.length > 0) {
      // A juntada já foi confirmada; atualizar documentos não deve transformar sucesso em reenvio.
      void dispatch(listarDocumentosPainel(input.id));
    }
    // Sem confirmação da leitura, o reducer conserva o recibo e o card de resposta publicada.
    const leitura = await dispatch(listarAndamentosPainel(input.id));
    if (!envioAtual())
      return rejectWithValue(
        'A sessão mudou durante a atualização. Consulte o resultado na sessão de origem.',
      );
    return listarAndamentosPainel.fulfilled.match(leitura) &&
      getState().arbitro.andamentosRequestId === leitura.meta.requestId
      ? leitura.payload
      : null;
  },
  {
    condition: (input, { getState }) => {
      const state = getState().arbitro;
      return (
        !state.isCriandoAndamento &&
        !(input.pendenciaId && state.respostasPendencias[input.id]?.[input.pendenciaId])
      );
    },
  },
);

export const listarDocumentosPainel = createAsyncThunk<
  PainelDocumento[],
  string,
  { rejectValue: string }
>('arbitro/listarDocumentosPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarDocumentos(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const baixarDocumentoPainel = createAsyncThunk<
  string,
  BaixarDocumentoInput,
  { rejectValue: string }
>('arbitro/baixarDocumentoPainel', async (input, { rejectWithValue }) => {
  try {
    return await painelProcessosService.baixarDocumento(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const listarCertificadosPainel = createAsyncThunk<
  CertificadoGerado[],
  string,
  { rejectValue: string }
>('arbitro/listarCertificadosPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarCertificados(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const baixarCertificadoPainel = createAsyncThunk<
  string,
  BaixarCertificadoInput,
  { rejectValue: string }
>('arbitro/baixarCertificadoPainel', async (input, { rejectWithValue }) => {
  try {
    return await painelProcessosService.baixarCertificado(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const obterCertificadoAcessosPainel = createAsyncThunk<
  CertificadoAcessosArbitral,
  string,
  { rejectValue: string }
>('arbitro/obterCertificadoAcessosPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obterCertificadoAcessos(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// Emite o termo e recarrega o detalhe (para refletir status dos termos + desbloquear as
// ações). Devolve o detalhe atualizado para o slice substituir o detalheItem.
export const emitirTermoImparcialidadePainel = createAsyncThunk<
  ObterProcessoResult,
  string,
  { rejectValue: string }
>('arbitro/emitirTermoImparcialidadePainel', async (id, { rejectWithValue }) => {
  try {
    await painelProcessosService.emitirTermoImparcialidade(id);
    return await painelProcessosService.obter(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const listarPendenciasPainel = createAsyncThunk<
  PainelPendencia[],
  string,
  { rejectValue: string }
>('arbitro/listarPendenciasPainel', async (id, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarPendencias(id);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const obterContextoConclusoPainel = createAsyncThunk<
  ConclusoRespostaContexto | null,
  { id: string; pendenciaId: string },
  { rejectValue: string }
>('arbitro/obterContextoConclusoPainel', async ({ id, pendenciaId }, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obterContextoConcluso(id, pendenciaId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// ===== Painel inicial do árbitro (dashboard) =====

export const obterDashboardPainel = createAsyncThunk<
  PainelDashboardResponse,
  void,
  { rejectValue: string }
>('arbitro/obterDashboardPainel', async (_void, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obterDashboard();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// ===== Painel financeiro do árbitro (extrato de repasses) =====

export const obterFinanceiroPainel = createAsyncThunk<
  PainelFinanceiroResponse,
  void,
  { rejectValue: string }
>('arbitro/obterFinanceiroPainel', async (_void, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obterFinanceiro();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// ===== Central/sino de notificações do árbitro =====

export const listarNotificacoesPainel = createAsyncThunk<
  PainelNotificacaoFeed,
  void,
  { rejectValue: string }
>('arbitro/listarNotificacoesPainel', async (_void, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarNotificacoes();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const marcarAvisoLidoPainel = createAsyncThunk<
  { recipienteId: string },
  string,
  { rejectValue: string }
>('arbitro/marcarAvisoLidoPainel', async (recipienteId, { rejectWithValue }) => {
  try {
    await painelProcessosService.marcarAvisoLido(recipienteId);
    return { recipienteId };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const marcarAvisosLidosPainel = createAsyncThunk<void, void, { rejectValue: string }>(
  'arbitro/marcarAvisosLidosPainel',
  async (_void, { rejectWithValue }) => {
    try {
      await painelProcessosService.marcarAvisosLidos();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarTermosPendentesPainel = createAsyncThunk<
  PainelProcessoItem[],
  void,
  { rejectValue: string }
>('arbitro/listarTermosPendentesPainel', async (_void, { rejectWithValue }) => {
  try {
    return await painelProcessosService.listarTermosPendentes();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// Emite em lote e recarrega os pendentes (o banner/modal refletem o que restou).
export const emitirTermosLotePainel = createAsyncThunk<
  PainelProcessoItem[],
  string[],
  { rejectValue: string }
>('arbitro/emitirTermosLotePainel', async (ids, { rejectWithValue }) => {
  try {
    await painelProcessosService.emitirTermosImparcialidadeLote(ids);
    return await painelProcessosService.listarTermosPendentes();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// ===== Emissão de documento (sentença/despacho) =====

export const listarModelosEmissaoPainel = createAsyncThunk<
  ModelosEmissaoPagina,
  ListarModelosEmissaoInput,
  { rejectValue: string; state: RootState }
>(
  'arbitro/listarModelosEmissaoPainel',
  async ({ id, categoria, page = 1 }, { rejectWithValue }) => {
    try {
      return await painelProcessosService.listarModelosEmissao(id, categoria, page);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
  {
    condition: ({ id, categoria, page = 1 }, { getState }) => {
      if (page === 1) return true;
      const modelos = getState().arbitro.modelosEmissao;
      return (
        modelos.processoId === id &&
        modelos.categoria === categoria &&
        !modelos.loading &&
        page === modelos.pagination.page + 1 &&
        page <= modelos.pagination.totalPages
      );
    },
  },
);

export const previewModeloEmissaoPainel = createAsyncThunk<
  EmissaoModeloPreview,
  { id: string; templateVersaoId: string; dadosContrato?: DadosContratoConferidos },
  { rejectValue: string }
>(
  'arbitro/previewModeloEmissaoPainel',
  async ({ id, templateVersaoId, dadosContrato }, { rejectWithValue }) => {
    try {
      return await painelProcessosService.previewModeloEmissao(id, templateVersaoId, dadosContrato);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const obterRascunhoEmissaoPainel = createAsyncThunk<
  EmissaoRascunho | null,
  { id: string; categoria: EmissaoCategoria },
  { rejectValue: string }
>('arbitro/obterRascunhoEmissaoPainel', async ({ id, categoria }, { rejectWithValue }) => {
  try {
    return await painelProcessosService.obterRascunhoEmissao(id, categoria);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const salvarRascunhoEmissaoPainel = createAsyncThunk<
  EmissaoRascunho,
  { id: string; body: SalvarRascunhoInput },
  { rejectValue: string }
>('arbitro/salvarRascunhoEmissaoPainel', async ({ id, body }, { rejectWithValue }) => {
  try {
    return await painelProcessosService.salvarRascunhoEmissao(id, body);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const publicarDocumentoEmissaoPainel = createAsyncThunk<
  void,
  { id: string; body: PublicarDocumentoInput },
  { rejectValue: string }
>('arbitro/publicarDocumentoEmissaoPainel', async ({ id, body }, { rejectWithValue }) => {
  try {
    await painelProcessosService.publicarDocumentoEmissao(id, body);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const listarProcessosArbitro = createAsyncThunk(
  'arbitro/listarProcessos',
  async (_void: void, { rejectWithValue }) => {
    try {
      return await arbitroWorkspaceService.listarProcessos();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
